import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

// Center modal — DESIGN-SYSTEM.md §5.6 "Center modal" (scrim + box 20px +
// header ícone-badge/título/descrição + X + corpo rolável + rodapé com ações
// à direita), mesmo padrão real já usado por `pages/services/View.js`,
// `pages/vehicles/checklist/ChecklistHistoryModal.js` e
// `pages/cashregisters/components/CenterModal.js`. `DialogForm` é o modal de
// confirmação/prompt genérico do sistema (usado em 36 telas) — só a casca
// visual foi trocada aqui, a API de props permanece 100% igual.
const ICON_BY_TYPE = {
    warning: AlertTriangle,
    error: XCircle,
    success: CheckCircle2,
    info: Info,
};

const TONE_BY_TYPE = {
    warning: { bg: 'warning.bg', color: 'warning.main' },
    error: { bg: 'error.bg', color: 'error.main' },
    success: { bg: 'success.bg', color: 'success.main' },
    info: { bg: 'info.bg', color: 'primary.main' },
};

export default function DialogForm(props) {
    const theme = useTheme();
    // Abaixo de `sm` o modal vira bottom sheet (tema); tela cheia só em sm..md.
    const fullScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const handleSubmit = (event) => {
        event.preventDefault();
        props.onSubmit(event);
    };

    const getIconType = () => {
        if (props.iconType) return props.iconType;
        if (props.title?.toLowerCase().includes('erro') || props.title?.toLowerCase().includes('error')) return 'error';
        if (props.title?.toLowerCase().includes('sucesso') || props.title?.toLowerCase().includes('success')) return 'success';
        if (props.title?.toLowerCase().includes('aviso') || props.title?.toLowerCase().includes('warning')) return 'warning';
        return 'info';
    };

    const iconType = getIconType();
    const Icon = ICON_BY_TYPE[iconType] || Info;
    const tone = TONE_BY_TYPE[iconType] || TONE_BY_TYPE.info;
    const onClose = props.onCancel || props.onClose;

    return (
        <Dialog
            fullScreen={fullScreen}
            open={props.open}
            onClose={props.onClose}
            component="form"
            onSubmit={handleSubmit}
            fullWidth
            // `maxWidth` é opt-in — todo consumidor existente (36 telas) não
            // passa essa prop e continua exatamente em 'sm' (o tamanho padrão
            // do "Center modal" §5.6). Só formulários com mais campos por
            // linha (ex. `component/vehicle/ModalAdd.js`, que tem um grid de
            // até 4 colunas) precisam de um modal mais largo.
            maxWidth={props.maxWidth || 'sm'}
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
            aria-labelledby="dialog-form-title"
            aria-describedby="dialog-form-description"
        >
            {/* header — ícone-badge + título + subtítulo + X */}
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: tone.bg,
                        color: tone.color,
                        mb: '10px',
                    }}>
                        <Icon size={18} strokeWidth={2} />
                    </Box>
                    <Typography id="dialog-form-title" sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        {props.title}
                    </Typography>
                    {props.subtitle && (
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {props.subtitle}
                        </Typography>
                    )}
                </Box>
                <IconButton type="button" onClick={onClose} size="small" disabled={props.loading} aria-label="Fechar">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* body — corpo rolável, sem decoração */}
            <Box sx={{ p: '18px 22px', maxHeight: '65vh', overflowY: 'auto' }}>
                {props.loading && props.showLoadingContent ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            py: 4
                        }}
                    >
                        <CircularProgress size={28} />
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {props.loadingContentText || 'Carregando campos...'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {props.content && (
                            <Typography
                                id="dialog-form-description"
                                sx={{
                                    fontSize: '13.5px',
                                    lineHeight: 1.6,
                                    color: 'text.primary',
                                    mb: props.children ? 2 : 0,
                                }}
                            >
                                {props.content}
                            </Typography>
                        )}

                        {props.secondaryContent && (
                            <Typography
                                sx={{
                                    fontSize: '12.5px',
                                    lineHeight: 1.5,
                                    color: 'text.secondary',
                                    mt: props.content ? 1 : 0,
                                    mb: props.children ? 2 : 0,
                                }}
                            >
                                {props.secondaryContent}
                            </Typography>
                        )}

                        {props.children && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {props.children}
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* footer — ações à direita, botões padrão do tema */}
            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1.25,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
            }}>
                <Button
                    disabled={props.loading}
                    variant="outlined"
                    onClick={onClose}
                >
                    {props.cancelButtonText || 'Cancelar'}
                </Button>

                {!props.hideConfirmButton && (
                    <Button
                        type={props.onConfirm ? 'button' : 'submit'}
                        variant="contained"
                        disabled={props.loading}
                        onClick={props.onConfirm}
                        startIcon={props.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {props.loading ? (props.loadingText || 'Salvando...') : (props.confirmButtonText || 'Salvar')}
                    </Button>
                )}
            </Box>
        </Dialog>
    );
}
